import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { State } from '../redux/state';
import { userApiBasePath } from '../redux/selectors/userApi';
import HourlyPointsChart, { HourlyPointsBlock, HourlyChartView } from './HourlyPointsChart';
import { teamsLabelFor, campusLabelFor } from '../utils/competitionLabels';
import { Competition } from '../userApi';
import { withLocalization, LProps } from '../utils/wrappers';
import { SCOREBOARD_TREND_TOP_N } from './scoreboardChartView';
import './scoreboard.scss';
import './hourlyPointsChart.scss';

interface Props {
  competitionName: string;
  competition: Competition;
  apiBase: string;
  chartView: HourlyChartView;
}

interface LocalState {
  block?: HourlyPointsBlock;
  loading: boolean;
  error: string;
}

class ScoreboardPointsTrend extends React.Component<LProps<Props>, LocalState> {
  public state: LocalState = { loading: true, error: '' };

  public componentDidMount() {
    this.loadSeries();
  }

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.competitionName !== this.props.competitionName) {
      this.loadSeries();
    }
  }

  private loadSeries = () => {
    const { apiBase, competitionName } = this.props;
    if (!competitionName) {
      return;
    }
    this.setState({ loading: true, error: '' });
    fetch(`${apiBase}/competitions/${encodeURIComponent(competitionName)}/points-time-series`, {
      credentials: 'include'
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(String(res.status));
        }
        return res.json();
      })
      .then((block: HourlyPointsBlock) => {
        this.setState({ block, loading: false, error: '' });
      })
      .catch(() => {
        this.setState({ loading: false, error: 'unavailable', block: undefined });
      });
  }

  public render() {
    const { t, competition, chartView } = this.props;
    const { block, loading, error } = this.state;
    if (loading && !block) {
      return <p className="cc__scoreboard_trend__meta">{t('Loading chart…')}</p>;
    }
    if (error || !block) {
      return null;
    }
    const teamLabel = teamsLabelFor(competition, t('Division'));
    const departmentLabel = campusLabelFor(competition, t('Department'));
    const title = chartView === 'participant'
      ? t('Top participants')
      : chartView === 'division'
        ? t('Top {{label}}', { label: teamLabel })
        : t('Top {{label}}', { label: departmentLabel });
    return (
      <div className="cc__scoreboard_trend">
        <div className="cc__scoreboard_trend__title">{title}</div>
        <HourlyPointsChart
          block={block}
          view={chartView}
          teamLabel={teamLabel}
          compact={true}
          maxSeries={SCOREBOARD_TREND_TOP_N}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Partial<Props> => ({
  competitionName: state.competition && state.competition.name,
  competition: state.competition,
  apiBase: userApiBasePath(state)
});

export default compose(
  withLocalization('scoreboard'),
  connect(mapStateToProps)
)(ScoreboardPointsTrend) as React.ComponentClass<{ chartView: HourlyChartView }>;
